import { c as _c } from "react/compiler-runtime";
import React from 'react';
import { Button, ButtonProps, TextInput, TextInputProps } from '@mantine/core';
import { zodResolver } from '@mantine/form';
import { Google } from '../icons/Google';
import { z } from 'zod';
import { env } from 'utils/env';
import { compactObj } from 'shared/collection';
import { uri } from 'shared/uri';
import { sb } from 'utils/db';
import { NextRouter } from 'next/router';
import { AuthToken } from 'shared/forecast/license';
import { parseCliRedirect } from './cliRedirect';
export type SignInMode = 'anon-signup' | 'signup' | 'signin';
const zPassword = z.string().min(6, {
  message: 'Must have at least 6 characters'
});
export const zEmailForm = z.object({
  email: z.string().email({
    message: 'Invalid email'
  }),
  password: zPassword
});
export const zEmailFormSignup = zEmailForm.extend({
  password: z.union([z.literal(''), zPassword])
});
export const emailFormProps = (mode: AuthFormMode) => ({
  initialValues: {
    email: '',
    password: ''
  } as EmailFormValues,
  validate: zodResolver(mode == 'signup' ? zEmailFormSignup : zEmailForm),
  validateInputOnBlur: true
});
export type EmailFormValues = z.infer<typeof zEmailForm>;
export type AuthFormMode = 'signup' | 'signin';
export const EmailPassInput = t0 => {
  const $ = _c(22);
  const {
    getInputProps,
    mode,
    formRef,
    size: t1
  } = t0;
  const size = t1 === undefined ? "md" : t1;
  let t2;
  if ($[0] !== getInputProps) {
    t2 = getInputProps("email");
    $[0] = getInputProps;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let emailOnBlur;
  let emailProps;
  if ($[2] !== t2) {
    ({
      onBlur: emailOnBlur,
      ...emailProps
    } = t2);
    $[2] = t2;
    $[3] = emailOnBlur;
    $[4] = emailProps;
  } else {
    emailOnBlur = $[3];
    emailProps = $[4];
  }
  let t3;
  if ($[5] !== emailOnBlur || $[6] !== formRef) {
    t3 = e => {
      if (formRef?.current != null && !formRef.current.contains(e.relatedTarget)) {
        return;
      }
      emailOnBlur?.(e);
    };
    $[5] = emailOnBlur;
    $[6] = formRef;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== emailProps || $[9] !== size || $[10] !== t3) {
    t4 = <TextInput autoFocus={true} type="email" autoComplete="email" label="Email" variant="filled" className="font-semibold" size={size} {...emailProps} onBlur={t3} />;
    $[8] = emailProps;
    $[9] = size;
    $[10] = t3;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const t5 = mode == "signup" ? "new-password" : "current-password";
  const t6 = mode == "signup" ? "New password (optional)" : "Password";
  let t7;
  if ($[12] !== getInputProps) {
    t7 = getInputProps("password");
    $[12] = getInputProps;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== size || $[15] !== t5 || $[16] !== t6 || $[17] !== t7) {
    t8 = <TextInput type="password" autoComplete={t5} label={t6} variant="filled" size={size} {...t7} />;
    $[14] = size;
    $[15] = t5;
    $[16] = t6;
    $[17] = t7;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== t4 || $[20] !== t8) {
    t9 = <>{t4}{t8}</>;
    $[19] = t4;
    $[20] = t8;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  return t9;
};
export const SignInGoogleButton = t0 => {
  const $ = _c(10);
  let children;
  let onClick;
  let props;
  if ($[0] !== t0) {
    ({
      onClick,
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = onClick;
    $[3] = props;
  } else {
    children = $[1];
    onClick = $[2];
    props = $[3];
  }
  let t1;
  let t2;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      root: {
        height: "3.5em"
      }
    };
    t2 = <Google />;
    $[4] = t1;
    $[5] = t2;
  } else {
    t1 = $[4];
    t2 = $[5];
  }
  const t3 = children ?? "Sign in with Google";
  let t4;
  if ($[6] !== onClick || $[7] !== props || $[8] !== t3) {
    t4 = <Button variant="outline" className="py-4!" styles={t1} leftSection={t2} onClick={onClick} {...props}>{t3}</Button>;
    $[6] = onClick;
    $[7] = props;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
};
export const isSignInPath = (path: string) => ['/signin', '/signup'].includes(path);
export const signInRedirectUrl = (query?: {
  [key: string]: string | undefined;
}) => {
  const {
    origin,
    pathname,
    href
  } = typeof window !== 'undefined' ? window.location : {
    origin: env.URL,
    href: env.URL,
    pathname: ''
  };
  const url = uri(isSignInPath(pathname) ? origin : href);
  return url.with({
    hash: undefined,
    query: compactObj({
      ...url.query(),
      server_error: undefined,
      error_description: undefined,
      ...query
    })
  }) // remove the hashes as supabase uses this for access tokens
  .mergeQuery(query ?? {});
};
export const handleCliRedirect = async (router: NextRouter, redirectTo?: string) => {
  const redirectUrl = parseCliRedirect(redirectTo);
  if (!redirectUrl) return false;
  try {
    const {
      data: {
        session
      }
    } = await sb.auth.getSession();
    if (!session) return false;
    const {
      access_token,
      refresh_token
    } = session;
    const cliUrl = uri(redirectUrl.toString()).mergeQuery({
      sb_url: env.NEXT_PUBLIC_SUPABASE_URL,
      access_token,
      refresh_token
    } satisfies AuthToken).url;

    // Redirect back to the validated loopback CLI with tokens.
    window.location.href = cliUrl;
    return true;
  } catch (error) {
    console.error('Error handling CLI redirect:', error);
    return false;
  }
};